import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, fmtMoney } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Loader2, Download, FileSignature } from "lucide-react";

const STATUS_LABELS = {
  pending_payment: { text: "Pending Payment", color: "text-amber-700" },
  pending_signature: { text: "Awaiting Signature", color: "text-amber-700" },
  active: { text: "Active", color: "text-emerald-700" },
  cancelled: { text: "Cancelled", color: "text-red-700" },
};

export default function BookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => navigate("/"));
  }, [bookingId, navigate]);

  if (!booking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;

  const status = STATUS_LABELS[booking.status] || { text: booking.status, color: "text-[#0B0C0E]" };
  const pdfUrl = `${API}/bookings/${bookingId}/lease.pdf`;

  return (
    <div data-testid="booking-detail-page" className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="overline">Booking</div>
        <h1 className="mt-2 font-display font-black text-4xl uppercase tracking-tight text-[#0B0C0E]">Lot {booking.lot_number} · {booking.tenant_name}</h1>
        <div className={`mt-3 ${status.color} uppercase tracking-wide text-sm font-bold`} data-testid="booking-status">{status.text}</div>

        <div className="mt-8 grid sm:grid-cols-2 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E]">
          <Cell label="Tenant" value={booking.tenant_name} />
          <Cell label="Email" value={booking.tenant_email} />
          <Cell label="Phone" value={booking.tenant_phone} />
          <Cell label="Company" value={booking.company || "—"} />
          <Cell label="Lot" value={`#${booking.lot_number}`} />
          <Cell label="Monthly Rent" value={fmtMoney(booking.monthly_rent)} />
          <Cell label="Deposit" value={fmtMoney(booking.security_deposit)} />
          <Cell label="Term" value={`${booking.lease_term_months} months`} />
          <Cell label="Start Date" value={booking.start_date} />
          <Cell label="Total Paid" value={booking.payment_status === "paid" ? fmtMoney(booking.total_due) : "Pending"} />
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          {booking.status === "pending_signature" && (
            <button onClick={() => navigate(`/booking/${bookingId}/sign`)} className="bg-[#0B0C0E] text-white font-bold px-8 py-3 uppercase inline-flex items-center gap-2 border-l-4 border-[#FACC15]" data-testid="goto-sign">
              <FileSignature className="w-4 h-4" /> Sign Lease
            </button>
          )}
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] px-8 py-3 uppercase text-sm inline-flex items-center gap-2 font-bold" data-testid="download-lease">
            <Download className="w-4 h-4" /> Download Lease PDF
          </a>
        </div>
      </div>
    </div>
  );
}

const Cell = ({ label, value }) => (
  <div className="bg-white p-5">
    <div className="overline">{label}</div>
    <div className="mt-1 text-[#0B0C0E]">{value}</div>
  </div>
);
